import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

import { API } from '../../../config';
import CourierLayout from '../CourierLayout';

function getToken() {
  return localStorage.getItem('courier_token') || localStorage.getItem('token');
}

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function normalizeOfferRole(role) {
  const value = String(role || '').toLowerCase();

  if (value.includes('customer')) {
    return 'customer';
  }

  if (
    value.includes('courier') ||
    value.includes('driver') ||
    value.includes('carrier')
  ) {
    return 'carrier';
  }

  return value;
}

function roleLabel(role) {
  const normalized = normalizeOfferRole(role);

  if (normalized === 'customer') {
    return 'Customer';
  }

  if (normalized === 'carrier') {
    return 'Carrier';
  }

  return 'User';
}

function isPendingStatus(status) {
  return String(status || '').toUpperCase() === 'PENDING';
}

function isAcceptedStatus(status) {
  return String(status || '').toUpperCase() === 'ACCEPTED';
}

function isRejectedStatus(status) {
  return String(status || '').toUpperCase() === 'REJECTED';
}

function isCustomerOffer(offer) {
  return normalizeOfferRole(offer?.proposer_role) === 'customer';
}

function isCarrierOffer(offer) {
  return normalizeOfferRole(offer?.proposer_role) === 'carrier';
}

function offerTitleForCarrier(offer) {
  return isCarrierOffer(offer) ? 'Your Offer' : 'Customer Offer';
}

function offerStatusChipColor(status) {
  if (isPendingStatus(status)) {
    return 'warning';
  }

  if (isAcceptedStatus(status)) {
    return 'success';
  }

  if (isRejectedStatus(status)) {
    return 'error';
  }

  return 'default';
}

async function getResponseError(response, fallbackMessage) {
  const data = await response.json().catch(() => ({}));

  return (
    data?.message ||
    data?.error?.message ||
    fallbackMessage ||
    `Request failed (${response.status})`
  );
}

const CourierNegotiationThread = () => {
  const { threadId } = useParams();
  const history = useHistory();

  const [messages, setMessages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [threadInfo, setThreadInfo] = useState(null);

  const [msgBody, setMsgBody] = useState('');
  const [offerAmount, setOfferAmount] = useState('');
  const [offerNotes, setOfferNotes] = useState('');

  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const pendingOffer = useMemo(
    () => offers.find((offer) => isPendingStatus(offer.status)),
    [offers]
  );

  const pendingOfferFromCarrier = Boolean(
    pendingOffer && isCarrierOffer(pendingOffer)
  );

  const pendingOfferFromCustomer = Boolean(
    pendingOffer && isCustomerOffer(pendingOffer)
  );

  const offerInputsDisabled = Boolean(
    pendingOffer && !pendingOfferFromCustomer
  );

  const loadLabel =
    threadInfo?.order_number ||
    (threadInfo?.order_id ? `ORD-${threadInfo.order_id}` : '');

  const customerLabel = threadInfo?.customer_name || '';

  const loadAll = async () => {
    try {
      setErr('');

      // Load thread summary so the driver can see the real load/order number.
      // This is non-blocking: messages and offers still load even if summary fails.
      try {
        const summaryResponse = await fetch(
          `${API}/api/negotiations/thread/${threadId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );

        if (summaryResponse.ok) {
          const summary = await summaryResponse.json();
          setThreadInfo(summary);
        }
      } catch (summaryError) {
        console.warn(
          'Unable to load negotiation thread summary:',
          summaryError
        );
      }

      const [messagesResponse, offersResponse] = await Promise.all([
        fetch(`${API}/api/negotiations/thread/${threadId}/messages`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }),
        fetch(`${API}/api/negotiations/thread/${threadId}/offers`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }),
      ]);

      if (!messagesResponse.ok) {
        throw new Error(
          await getResponseError(
            messagesResponse,
            `Unable to load messages (${messagesResponse.status})`
          )
        );
      }

      if (!offersResponse.ok) {
        throw new Error(
          await getResponseError(
            offersResponse,
            `Unable to load offers (${offersResponse.status})`
          )
        );
      }

      const messagesData = await messagesResponse.json();
      const offersData = await offersResponse.json();

      setMessages(
        Array.isArray(messagesData)
          ? messagesData
          : messagesData.messages || messagesData.rows || []
      );

      setOffers(
        Array.isArray(offersData)
          ? offersData
          : offersData.offers || offersData.rows || []
      );
    } catch (error) {
      setErr(error.message || 'Unable to load negotiation.');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const initialize = async () => {
      setLoading(true);
      await loadAll();
      setLoading(false);
    };

    initialize();

    const timer = setInterval(() => {
      loadAll();
    }, 6000);

    return () => clearInterval(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const sendMessage = async () => {
    try {
      setErr('');

      const body = msgBody.trim();

      if (!body) {
        return;
      }

      const response = await fetch(
        `${API}/api/negotiations/thread/${threadId}/messages`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            body,
            message: body,
            content: body,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getResponseError(
            response,
            `Unable to send message (${response.status})`
          )
        );
      }

      setMsgBody('');
      await loadAll();
    } catch (error) {
      setErr(error.message || 'Unable to send message.');
    }
  };

  const createOffer = async () => {
    try {
      setErr('');

      if (pendingOfferFromCarrier) {
        setErr(
          'Your offer is already pending. Wait for the customer to respond.'
        );
        return;
      }

      if (pendingOfferFromCustomer) {
        setErr(
          'A customer offer is pending. Enter a counter amount and press Counter on that customer offer.'
        );
        return;
      }

      const dollars = Number(offerAmount);

      if (!Number.isFinite(dollars) || dollars <= 0) {
        setErr('Enter a valid offer amount.');
        return;
      }

      const amount_cents = Math.round(dollars * 100);
      const noteText = offerNotes.trim() || null;

      const response = await fetch(
        `${API}/api/negotiations/thread/${threadId}/offers`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount_cents,
            amountCents: amount_cents,
            amount: amount_cents,
            note: noteText,
            notes: noteText,
            message: noteText,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getResponseError(
            response,
            `Unable to send offer (${response.status})`
          )
        );
      }

      setOfferAmount('');
      setOfferNotes('');
      await loadAll();
    } catch (error) {
      setErr(error.message || 'Unable to send offer.');
    }
  };

  const acceptOffer = async (offerId) => {
    try {
      setErr('');

      const response = await fetch(
        `${API}/api/negotiations/offers/${offerId}/accept`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          await getResponseError(
            response,
            `Unable to accept offer (${response.status})`
          )
        );
      }

      alert('Customer offer accepted. The negotiated rate is now locked.');
      await loadAll();
    } catch (error) {
      setErr(error.message || 'Unable to accept offer.');
    }
  };

  const rejectOffer = async (offerId) => {
    try {
      setErr('');

      const response = await fetch(
        `${API}/api/negotiations/offers/${offerId}/reject`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          await getResponseError(
            response,
            `Unable to reject offer (${response.status})`
          )
        );
      }

      await loadAll();
    } catch (error) {
      setErr(error.message || 'Unable to reject offer.');
    }
  };

  const counterOffer = async (offerId) => {
    try {
      setErr('');

      const dollars = Number(offerAmount);

      if (!Number.isFinite(dollars) || dollars <= 0) {
        setErr('Enter a valid counter amount in the offer amount field.');
        return;
      }

      const amount_cents = Math.round(dollars * 100);
      const noteText = offerNotes.trim() || null;

      const response = await fetch(
        `${API}/api/negotiations/offers/${offerId}/counter`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount_cents,
            amountCents: amount_cents,
            amount: amount_cents,
            note: noteText,
            notes: noteText,
            message: noteText,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getResponseError(
            response,
            `Unable to send counter offer (${response.status})`
          )
        );
      }

      setOfferAmount('');
      setOfferNotes('');
      await loadAll();
    } catch (error) {
      setErr(error.message || 'Unable to send counter offer.');
    }
  };

  return (
    <CourierLayout>
      <Box sx={{ width: '100%', overflowX: 'hidden' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, sm: 3, md: 4 },
            borderRadius: 3,
            maxWidth: 1100,
            mx: 'auto',
            border: '1px solid #e2e8f0',
            boxShadow: {
              xs: 'none',
              md: '0 10px 24px rgba(15, 23, 42, 0.06)',
            },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              fontWeight={800}
              gutterBottom
              sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              Negotiation
            </Typography>

            <Typography variant="body1" color="text.secondary">
              Exchange messages, review offers, and negotiate rates for this load.
            </Typography>

            {loadLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Load {loadLabel}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Thread #{threadId}
            </Typography>

            {customerLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Customer: {customerLabel}
              </Typography>
            )}
          </Box>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}

          {loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading negotiation details...
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#fff',
                  height: '100%',
                }}
              >
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Messages
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={{
                    height: { xs: 360, md: 420 },
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    p: 2,
                  }}
                >
                  {messages.length === 0 ? (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No messages yet.
                      </Typography>
                    </Box>
                  ) : (
                    messages.map((message) => (
                      <Box
                        key={message.id}
                        sx={{
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: '#ffffff',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            sx={{ color: '#0f172a' }}
                          >
                            {roleLabel(message.sender_role)}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {message.created_at
                              ? new Date(message.created_at).toLocaleString()
                              : ''}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#475569',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {message.body || message.message || message.content || ''}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    value={msgBody}
                    onChange={(event) => setMsgBody(event.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        sendMessage();
                      }
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={sendMessage}
                    sx={{
                      minWidth: 90,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    Send
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#fff',
                }}
              >
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Offers
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mb: 2 }}>
                  {offers.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 2 }}
                    >
                      No offers yet.
                    </Typography>
                  ) : (
                    offers.map((offer) => {
                      const offerId = offer.id || offer.offer_id;
                      const isOwnOffer = isCarrierOffer(offer);
                      const isCustomerPendingOffer =
                        isPendingStatus(offer.status) && isCustomerOffer(offer);

                      return (
                        <Paper
                          key={offerId}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#f8fafc',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 0.75,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={800}
                              sx={{ color: '#0f172a' }}
                            >
                              {offerTitleForCarrier(offer)}
                            </Typography>

                            <Chip
                              label={offer.status || 'Unknown'}
                              size="small"
                              color={offerStatusChipColor(offer.status)}
                              variant="outlined"
                              sx={{
                                height: 24,
                                fontSize: '0.72rem',
                                fontWeight: 700,
                              }}
                            />
                          </Box>

                          <Typography
                            variant="h6"
                            fontWeight={800}
                            sx={{ color: '#0f172a', mb: 0.5 }}
                          >
                            {money(offer.amount_cents)}
                          </Typography>

                          {(offer.notes || offer.note || offer.message) && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#475569',
                                mb: 1,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              Notes: {offer.notes || offer.note || offer.message}
                            </Typography>
                          )}

                          {isOwnOffer && isPendingStatus(offer.status) && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              Waiting for the customer to respond.
                            </Alert>
                          )}

                          {isCustomerPendingOffer && (
                            <Grid container spacing={1} sx={{ mt: 1.5 }}>
                              <Grid item xs={12}>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={() => acceptOffer(offerId)}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 2,
                                  }}
                                >
                                  Accept Customer Offer
                                </Button>
                              </Grid>

                              <Grid item xs={12}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  onClick={() => rejectOffer(offerId)}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 2,
                                  }}
                                >
                                  Reject
                                </Button>
                              </Grid>

                              <Grid item xs={12}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  onClick={() => counterOffer(offerId)}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 2,
                                  }}
                                >
                                  Counter
                                </Button>
                              </Grid>
                            </Grid>
                          )}
                        </Paper>
                      );
                    })
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                  {pendingOfferFromCarrier
                    ? 'Your pending offer'
                    : pendingOfferFromCustomer
                      ? 'Counter customer offer'
                      : 'Send offer'}
                </Typography>

                {pendingOfferFromCarrier && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Your offer is pending. Wait for the customer to accept,
                    reject, or counter.
                  </Alert>
                )}

                {pendingOfferFromCustomer && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Enter a counter amount and optional notes below, then press
                    Counter on the customer offer.
                  </Alert>
                )}

                <TextField
                  fullWidth
                  size="small"
                  label="Offer Amount"
                  placeholder="145.00"
                  value={offerAmount}
                  onChange={(event) => setOfferAmount(event.target.value)}
                  disabled={offerInputsDisabled}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ mb: 1.5 }}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  placeholder="Optional message"
                  value={offerNotes}
                  onChange={(event) => setOfferNotes(event.target.value)}
                  disabled={offerInputsDisabled}
                  multiline
                  minRows={2}
                  sx={{ mb: 1.5 }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={createOffer}
                  disabled={Boolean(pendingOffer)}
                  sx={{
                    minHeight: 42,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    mb: 1,
                  }}
                >
                  {pendingOfferFromCustomer ? 'Use Counter Button Above' : 'Send Offer'}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => history.push('/courier/order-management')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  Back to My Loads
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => history.push('/courier/available-orders')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  Back to Available Loads
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </CourierLayout>
  );
};

export default CourierNegotiationThread;